import React, { useEffect, useMemo, useRef, useState } from 'react'
import SegmentControlUpdated from 'app/views/components/segments/segment-control-updated'
import { Group, Stack } from '@mantine/core'
import KPI_Section from 'app/views/components/KPI/KPI-horizontal-section'
import { positionsKPIData } from './positions-kpi-data'
import { useDispatch, useSelector } from 'react-redux'
import { loadPositionsScreen } from 'app/state/ducks/positions-screen/thunks'
import { getPositionsScreenLoader, getPositionsScreenRows } from 'app/state/ducks/positions-screen/selectors'
import { positionsForLender } from 'app/state/ducks/lenders-positions/thunks'
import { LendersPosition, LendersPositionWithMapping } from 'app/models/lenders-position-params'
import { toGridRows } from './positions-util'
import MainTable from 'app/views/components/Table/MainTable'
import { colsFacilitiesPositions } from './positions-facilities-columnDefs'
import { colsDealsPositions } from './positions-deals-columnDefs'
import { colsEntitiesPositions } from './positions-entities-columnDefs'
import { MRT_ColumnDef } from 'mantine-react-table'
import PositionDrawer from './position-drawer'
import { GridSortingStorage, GridFilterStorage, GridPaginationStorage, GridColumnVisibilityStorage, useGridSorting, useGridFilters, useGridPagination, useGridColumnVisibility } from 'app/utils/grid-sorting-storage'
import { useLocation } from 'react-router-dom'

export function PositionsDrillPage() {
  const dispatch = useDispatch()
  const location = useLocation()

  const tabs: Record<string, { route: string; label: string; columns: MRT_ColumnDef<LendersPositionWithMapping>[] }> = {
    positionsDeal: { route: '/positions/deal', label: 'DEALS', columns: colsDealsPositions },
    positionsFacility: { route: '/positions/facility', label: 'FACILITIES', columns: colsFacilitiesPositions },
    positionsEntity: { route: '/positions/entity', label: 'ENTITIES', columns: colsEntitiesPositions },
  }

  const tab = Object.entries(tabs).find(([, t]) => t.route === location.pathname)?.[0]
    ?? Object.keys(tabs)[0]

  // ONE request for the screen (ADR_LOS-030): the rows arrive with every name,
  // the amounts and the past-due flag resolved, so the page loads no list.
  const rows = useSelector(getPositionsScreenRows)
  const loading = useSelector(getPositionsScreenLoader)
  const [drawer, setDrawer] = useState<boolean>(false)
  const [drawerTitle, setDrawerTitle] = useState('')
  const [lendersPosition, setLendersPosition] = useState<LendersPosition>()
  const [drawerState, setDrawerState] = useState<'loading' | 'ready' | 'missing'>('loading')
  // the row whose history the drawer is showing: a read for an earlier row
  // that lands after a later click must not replace the later row's history
  const openRowId = useRef<string | undefined>(undefined)

  // Grid preferences for Deals tab
  const dealsSorting = useGridSorting(GridSortingStorage.positionsDeal)
  const dealsFilters = useGridFilters(GridFilterStorage.positionsDeal)
  const dealsPagination = useGridPagination(GridPaginationStorage.positionsDeal)
  const dealsColumnVisibility = useGridColumnVisibility(
    GridColumnVisibilityStorage.positionsDeal,
    ['dealId', 'dealClientId', 'facilityId', 'facilitySystemId', 'facilityClientId', 'entityId', 'entityInternalId']
  )

  // Grid preferences for Facilities tab
  const facilitiesSorting = useGridSorting(GridSortingStorage.positionsFacility)
  const facilitiesFilters = useGridFilters(GridFilterStorage.positionsFacility)
  const facilitiesPagination = useGridPagination(GridPaginationStorage.positionsFacility)
  const facilitiesColumnVisibility = useGridColumnVisibility(
    GridColumnVisibilityStorage.positionsFacility,
    ['dealId', 'dealClientId', 'facilityId', 'facilitySystemId', 'facilityClientId', 'entityId', 'entityInternalId']
  )

  // Grid preferences for Entities tab
  const entitiesSorting = useGridSorting(GridSortingStorage.positionsEntity)
  const entitiesFilters = useGridFilters(GridFilterStorage.positionsEntity)
  const entitiesPagination = useGridPagination(GridPaginationStorage.positionsEntity)
  const entitiesColumnVisibility = useGridColumnVisibility(
    GridColumnVisibilityStorage.positionsEntity,
    ['dealId', 'dealClientId', 'facilityId', 'facilitySystemId', 'facilityClientId', 'entityId', 'entityInternalId']
  )

  useEffect(() => {
    dispatch(loadPositionsScreen() as any)
  }, [dispatch])

  // The drawer shows the position's history, which the grid row does not
  // carry (every row would ship every pro-rata): fetched when a row opens.
  async function handleRowClick(row: LendersPositionWithMapping) {
    openRowId.current = row.id
    setDrawerTitle(row.entityName)
    setLendersPosition(undefined)
    setDrawerState('loading')
    setDrawer(true)
    // positionsForLender answers [] on a failed request as well as on no position
    const positions: LendersPosition[] = await positionsForLender(row.entityId ?? '', [row.facilityId ?? ''])
    if (openRowId.current !== row.id) return
    const position = positions.find(p => p.contractId?.id === row.facilityId) ?? positions[0]
    setLendersPosition(position)
    setDrawerState(position ? 'ready' : 'missing')
  }

  const positionsWithMapping = useMemo(() => toGridRows(rows), [rows])
  const kpiData = positionsKPIData(positionsWithMapping)

  // Get grid preferences based on active tab
  const getGridPreferences = () => {
    switch (tab) {
      case 'positionsDeal':
        return {
          sorting: dealsSorting,
          filters: dealsFilters,
          pagination: dealsPagination,
          columnVisibility: dealsColumnVisibility,
        }
      case 'positionsFacility':
        return {
          sorting: facilitiesSorting,
          filters: facilitiesFilters,
          pagination: facilitiesPagination,
          columnVisibility: facilitiesColumnVisibility,
        }
      case 'positionsEntity':
        return {
          sorting: entitiesSorting,
          filters: entitiesFilters,
          pagination: entitiesPagination,
          columnVisibility: entitiesColumnVisibility,
        }
      default:
        return {
          sorting: dealsSorting,
          filters: dealsFilters,
          pagination: dealsPagination,
          columnVisibility: dealsColumnVisibility,
        }
    }
  }

  const gridPrefs = getGridPreferences()

  return (
    <>
      <Stack w="100%">
        <Group w="100%" position="apart" className='tabsGapsActivity'>
          <Group position="right">
            <SegmentControlUpdated tabs={tabs} />
          </Group>
        </Group>

        <KPI_Section
          kpiData={kpiData}
          isLoading={loading}
        />
        <MainTable
          key={tab}
          tableName='Positions Table'
          enableColumnResizing={false}
          withPagination={true}
          csvExportEnabled={true}
          enableTopToolbar={false}
          headerBackgroundColor='#F0EEEE'
          overflow="auto"
          minHeight="0px"
          columnDefs={tabs[tab].columns}
          data={positionsWithMapping}
          isLoading={loading}
          hiddenColumns={['dealId', 'dealClientId', 'facilityId', 'facilitySystemId', 'facilityClientId', 'entityId', 'entityInternalId']}
          initialSorting={[{ id: gridPrefs.sorting.sorting.sortBy, desc: gridPrefs.sorting.sorting.order === 'desc' }]}
          onSortingChange={gridPrefs.sorting.handleSortingChange}
          initialColumnFilters={gridPrefs.filters.toMRTColumnFilters()}
          onColumnFiltersChange={gridPrefs.filters.handleMRTFiltersChange}
          initialPagination={{ pageSize: gridPrefs.pagination.pagination.pageSize, pageIndex: gridPrefs.pagination.pagination.pageIndex }}
          onPaginationChange={gridPrefs.pagination.handlePaginationChange}
          initialColumnVisibility={gridPrefs.columnVisibility.columnVisibility}
          onColumnVisibilityChange={gridPrefs.columnVisibility.handleColumnVisibilityChange}
          setRow={(position: LendersPositionWithMapping) => handleRowClick(position)}
        />
      </Stack>
      <PositionDrawer
        setOpen={setDrawer}
        open={drawer}
        title={drawerTitle}
        state={drawerState}
        lendersPosition={lendersPosition}
      />
    </>
  )
}
